"use client";

import Link from "next/link";
import { useDayOut } from "@/app/_components/companion/DayOutSelection";
import { dayOutLocal, dayOutPlace } from "@/lib/companion/day-out";
import { useSearchParams } from "next/navigation";
import type { ReactNode } from "react";
import { ChevronRight, Film, MapPinned, MessageCircle, Trophy, UsersRound } from "lucide-react";
import type { CompanionHomeModel } from "@/lib/companion/home-model";
import {
  defaultRollCallDate,
  localDateId,
  type CompanionPeopleLoopData,
} from "@/lib/companion/people-loop";
import { SquadManagementCard } from "../SquadManagementCard";
import { CompanionMeetups } from "./CompanionMeetups";
import { CompanionPeopleFilters } from "./CompanionPeopleFilters";
import { CompanionRollCall } from "./CompanionRollCall";
import { CompanionSocialFeed } from "./CompanionSocialFeed";
import { CompanionStatusPins } from "./CompanionStatusPins";
import { UUID } from "@/lib/companion/outings";
import { OutingInvitations } from "./OutingInvitations";
import { CompanionConnections } from "./CompanionConnections";

export function CompanionPeopleWorkspace({
  model,
  people,
  renderedAt,
  viewerUserId,
  readOnlyFixture = false,
  connectionPreview = false,
}: {
  model: CompanionHomeModel;
  people: CompanionPeopleLoopData;
  renderedAt: string;
  viewerUserId: string | null;
  readOnlyFixture?: boolean;
  connectionPreview?: boolean;
}): ReactNode {
  const searchParams = useSearchParams();
  const { selection } = useDayOut();
  const selectedMeetupDate = selection ? dayOutLocal(selection).date : null;
  const renderedDate = new Date(renderedAt);
  const timezone = model.activeBase?.timezone ?? people.rollCalls[0]?.timezone ?? "UTC";
  const today = localDateId(renderedDate, timezone) || renderedAt.slice(0, 10);
  const tomorrow = shiftDate(today, 1);
  const queryDay = searchParams.get("day");
  const defaultDate = defaultRollCallDate(renderedDate, timezone).date ?? today;
  const selectedDate = queryDay === today || queryDay === tomorrow
    ? queryDay
    : defaultDate;
  const querySquadId = searchParams.get("squad");
  const selectedSquadId = people.squads.some((squad) => squad.id === querySquadId)
    ? querySquadId
    : null;
  const matchesSelection = (squadId: string, date?: string) =>
    (!selectedSquadId || squadId === selectedSquadId) && (!date || date === selectedDate);
  const rollCalls = people.rollCalls.filter((entry) => matchesSelection(entry.squadId, entry.targetLocalDate));
  const meetups = people.meetups.filter((meetup) => (!selectedSquadId || meetup.squadId === selectedSquadId) && meetup.targetLocalDate === (selectedMeetupDate ?? selectedDate));
  const statusPins = people.statusPins.filter((status) => matchesSelection(status.squadId, status.targetLocalDate));
  const moments = people.moments.filter((moment) => !selectedSquadId || moment.squadId === selectedSquadId);
  const place = model.visiblePlace ?? model.decisionPlace ?? model.allSavedPlaces[0] ?? null;
  const defaultPlace = place ? {
    baseId: place.baseId ?? model.activeBase?.id ?? null,
    slug: place.slug,
    name: place.name,
    sport: place.preferredSport ?? place.sport,
  } : null;
  const sourceUnavailable = (source: CompanionPeopleLoopData["unavailableSources"][number]) =>
    people.unavailableSources.includes(source);

  return (
    <div
      data-retention-id="companion-retention-people"
      className="space-y-5"
    >
      <nav aria-label="Make plans with your people" className="grid gap-3 sm:grid-cols-2">
        <PeopleShortcut href="/companion/people/chat" title="Chats" description="Friends, Squads and your resort" icon={MessageCircle} />
        <PeopleShortcut href="/companion/plan/together" title="Find time together" description="Find a free window with friends" icon={UsersRound} />
      </nav>
      <CompanionConnections
        viewerUserId={connectionPreview ? "88888888-8888-4888-8888-888888888888" : readOnlyFixture ? null : viewerUserId}
        defaultPlace={connectionPreview ? { slug: "tignes", name: "Tignes" } : defaultPlace?.slug ? { slug: defaultPlace.slug, name: defaultPlace.name } : null}
        developmentPreview={connectionPreview}
      />
      {!readOnlyFixture || connectionPreview ? <OutingInvitations owner={connectionPreview ? "88888888-8888-4888-8888-888888888888" : viewerUserId} developmentPreview={connectionPreview} invitationId={UUID.test(searchParams.get("invitation") ?? "") ? searchParams.get("invitation")! : undefined} /> : null}
      <section aria-label="Squad daily operations" className="rounded-lg bg-navy p-4 shadow-sm md:p-5">
        <CompanionPeopleFilters
          squads={people.squads}
          dayOut={selection}
          selectedSquadId={selectedSquadId}
          selectedDate={selectedDate}
          today={today}
          tomorrow={tomorrow}
          baseId={model.viewedBase?.id ?? null}
        />
        {sourceUnavailable("roll-call") ? (
          <p role="status" className="mt-5 rounded-sm border border-terracotta/50 bg-terracotta/15 px-3 py-2 text-[12px] text-white">
            Roll call is unavailable right now.
          </p>
        ) : (
          <CompanionRollCall
            entries={rollCalls}
            squads={people.squads}
            selectedDate={selectedDate}
            selectedSquadId={selectedSquadId}
            timezone={timezone}
            defaultPlace={defaultPlace}
            viewerUserId={viewerUserId}
          />
        )}
      </section>

      <div className="grid gap-5 lg:grid-cols-2">
        <CompanionMeetups
          meetups={meetups}
          squads={people.squads}
          selectedDate={selectedMeetupDate ?? selectedDate}
          selectedSquadId={selectedSquadId}
          timezone={selection?.timezone ?? timezone}
          defaultPlace={selection ? dayOutPlace(selection) : defaultPlace ? { slug: defaultPlace.slug, name: defaultPlace.name } : null}
          unavailable={sourceUnavailable("meetups")}
          viewerUserId={viewerUserId}
          dayOut={selection}
          readOnly={readOnlyFixture}
        />
        <CompanionStatusPins
          statusPins={statusPins}
          squads={people.squads}
          selectedDate={selectedDate}
          selectedSquadId={selectedSquadId}
          timezone={timezone}
          defaultPlace={defaultPlace ? { slug: defaultPlace.slug, name: defaultPlace.name } : null}
          unavailable={sourceUnavailable("statuses")}
          viewerUserId={viewerUserId}
        />
      </div>

      <LiveLocationBoundary />
      <CompanionSocialFeed moments={moments} unavailable={sourceUnavailable("moments")} />

      <div id="squads" className="scroll-mt-28">
        {model.status === "anonymous" ? (
          <PersonalProfileSquadBoundary />
        ) : (
          <SquadManagementCard squads={model.squads} />
        )}
      </div>
      <section aria-labelledby="people-discover-heading">
        <h2 id="people-discover-heading" className="mb-3 font-heading text-2xl font-bold text-navy">A little friendly competition</h2>
        <nav aria-label="Community activities" className="grid gap-3 sm:grid-cols-3">
          <PeopleShortcut href="/companion/challenges" title="Challenges" description="Distance, days out and trophies" icon={Trophy} />
          <PeopleShortcut href="/companion/piste-times" title="Piste times" description="Compare your recorded runs" icon={Trophy} />
          <PeopleShortcut href="/companion/people/freestyle/feed" title="Freestyle" description="Watch clips and share progress" icon={Film} />
        </nav>
      </section>
      <CompanionCommunityLinks />
      <LeaderboardSnapshot
        demoQualifyingMembers={people.demoLeaderboardQualifyingMembers}
      />
      <CrewDirectoryCard crews={model.crew} />
    </div>
  );
}

function PersonalProfileSquadBoundary(): ReactNode {
  return (
    <section
      aria-labelledby="personal-squads-heading"
      className="rounded-md border border-border-light bg-white p-4 md:p-5"
    >
      <p className="text-[11px] font-semibold uppercase tracking-[1.6px] text-gold">
        Squads
      </p>
      <h2 id="personal-squads-heading" className="mt-1 font-serif text-[26px] leading-tight text-navy">
        Your private group
      </h2>
      <p className="mt-2 text-[13px] leading-relaxed text-text-muted">
        Sign in to create a private Squad for your friends.
      </p>
    </section>
  );
}

export function CrewDirectoryCard({
  crews,
}: {
  crews: CompanionHomeModel["crew"];
}): ReactNode {
  return (
    <section aria-labelledby="crew-heading" className="rounded-md border border-border-light bg-white p-4 md:p-5">
      <p className="mb-1 text-[11px] font-semibold uppercase tracking-[1.6px] text-teal">
        Crews
      </p>
      <h2 id="crew-heading" className="font-serif text-[26px] leading-tight text-navy">
        Your season spaces
      </h2>
      {crews.length > 0 ? (
        <div className="mt-4 grid gap-3 sm:grid-cols-2">
          {crews.map((crew) => (
            <Link
              key={crew.id}
              href={crew.href}
              data-retention-route="/community/crew/[slug]"
              className="flex min-h-[72px] items-center gap-3 rounded-md border border-border-light bg-warm-bg p-3 no-underline hover:border-teal/50"
            >
              <span className="inline-flex h-10 w-10 items-center justify-center rounded-sm bg-white text-navy">
                <UsersRound className="h-5 w-5" aria-hidden="true" />
              </span>
              <span className="min-w-0">
                <span className="block truncate text-[14px] font-semibold text-navy">{crew.name}</span>
                <span className="mt-1 block text-[12.5px] text-text-muted">
                  {crew.unread ? "New Crew activity" : "Crew space ready"}
                </span>
              </span>
            </Link>
          ))}
        </div>
      ) : (
        <p className="mt-3 text-[13px] leading-relaxed text-text-muted">No Crew spaces yet. Find the destination and season community around you.</p>
      )}
    </section>
  );
}

function LiveLocationBoundary(): ReactNode {
  return (
    <section aria-labelledby="people-live-location-heading" className="rounded-md border border-teal/30 bg-teal/10 p-4 md:p-5">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <p className="text-[11px] font-semibold uppercase tracking-[1.6px] text-teal">Live map</p>
          <h2 id="people-live-location-heading" className="mt-1 font-serif text-[25px] leading-tight text-navy">Share only when you choose</h2>
          <p className="mt-2 max-w-[62ch] text-[13px] leading-relaxed text-text-body">Live location is separate and stays off until you start sharing.</p>
        </div>
        <Link href="/companion/track" className="inline-flex min-h-[44px] shrink-0 items-center justify-center gap-2 rounded-sm bg-navy px-4 text-[13px] font-semibold text-white no-underline hover:bg-navy-hover">
          <MapPinned className="h-4 w-4" aria-hidden="true" />
          Open live map
        </Link>
      </div>
    </section>
  );
}

function LeaderboardSnapshot({
  demoQualifyingMembers,
}: {
  demoQualifyingMembers?: number;
}): ReactNode {
  const previewCopy = demoQualifyingMembers === 1
    ? "Demo threshold state: one qualifying member. A second opted-in member is required."
    : demoQualifyingMembers !== undefined && demoQualifyingMembers >= 2
      ? "Demo threshold state: two qualifying members. The private Squad board can appear."
      : "Open daily, weekly, season and all-time boards. Private Squad boards require at least two opted-in members.";
  return (
    <section aria-labelledby="people-leaderboard-heading" className="rounded-md border border-border-light bg-white p-4 md:p-5">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <p className="text-[11px] font-semibold uppercase tracking-[1.6px] text-teal">Opt-in competition</p>
          <h2 id="people-leaderboard-heading" className="mt-1 font-serif text-[25px] leading-tight text-navy">Leaderboards</h2>
          <p
            data-testid={demoQualifyingMembers === undefined ? undefined : "people-leaderboard-threshold"}
            className="mt-2 text-[13px] text-text-muted"
          >
            {previewCopy}
          </p>
        </div>
        <Link
          href="/companion/leaderboards"
          data-retention-route="/companion/leaderboards"
          className="inline-flex min-h-[44px] shrink-0 items-center justify-center gap-2 rounded-sm border border-border bg-warm-bg px-4 text-[13px] font-semibold text-navy no-underline hover:border-teal/50"
        >
          <Trophy className="h-4 w-4 text-teal" aria-hidden="true" />
          Open leaderboards
        </Link>
      </div>
    </section>
  );
}

function CompanionCommunityLinks(): ReactNode {
  return (
    <section aria-labelledby="community-links-heading" className="rounded-md border border-border-light bg-white p-4 md:p-5">
      <p className="mb-1 text-[11px] font-semibold uppercase tracking-[1.6px] text-teal">Community</p>
      <h2 id="community-links-heading" className="font-serif text-[26px] leading-tight text-navy">Keep exploring</h2>
      <div className="mt-4 grid gap-3 sm:grid-cols-2">
        <Link
          href="/community/crew"
          data-retention-route="/community/crew"
          className="flex min-h-[44px] items-center gap-3 rounded-sm border border-border-light bg-warm-bg px-3 py-3 no-underline hover:border-teal/50"
        >
          <UsersRound className="h-4 w-4 shrink-0 text-teal" aria-hidden="true" />
          <span>
            <span className="block text-[13px] font-semibold text-navy">Crew directory</span>
            <span className="mt-0.5 block text-[11.5px] leading-snug text-text-muted">Find destination and season Crews.</span>
          </span>
        </Link>
        <Link
          href="/community/forum"
          className="flex min-h-[44px] items-center gap-3 rounded-sm border border-border-light bg-warm-bg px-3 py-3 no-underline hover:border-teal/50"
        >
          <MessageCircle className="h-4 w-4 shrink-0 text-teal" aria-hidden="true" />
          <span>
            <span className="block text-[13px] font-semibold text-navy">Forums</span>
            <span className="mt-0.5 block text-[11.5px] leading-snug text-text-muted">Open public community discussions.</span>
          </span>
        </Link>
      </div>
    </section>
  );
}

function shiftDate(dateId: string, days: number): string {
  const date = new Date(`${dateId}T12:00:00.000Z`);
  date.setUTCDate(date.getUTCDate() + days);
  return date.toISOString().slice(0, 10);
}

function PeopleShortcut({ href, title, description, icon: Icon }: {
  href: string;
  title: string;
  description: string;
  icon: typeof MessageCircle;
}) {
  return <Link href={href} className="group flex min-h-20 items-center gap-3 rounded-md border border-border bg-white p-4 no-underline transition-colors hover:border-teal focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-teal focus-visible:ring-offset-2">
    <Icon className="size-5 shrink-0 text-teal" aria-hidden="true" />
    <span className="min-w-0 flex-1"><span className="block text-base font-bold text-navy">{title}</span><span className="mt-1 block text-sm leading-snug text-text-muted">{description}</span></span>
    <ChevronRight className="size-4 shrink-0 text-text-muted group-hover:text-teal" aria-hidden="true" />
  </Link>;
}
